import React, { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button, Drawer, theme } from 'antd'
import { MenuOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { CampaignSettingsNavigationItem } from './campaign-settings-panels'
import './campaign-settings-shell.css'

type NavigationSize = 'small' | 'wide'

type CampaignSettingsShellProps = {
    items: CampaignSettingsNavigationItem[]
    activeKey: string
    children: ReactNode
    /** Label of the navigation; also used by the website pages. */
    title?: string
    /** Whether the page has the sticky campaign header above the settings; the navigation then sticks below it. */
    stickyHeader?: boolean
}

export function CampaignSettingsShell({
    items,
    activeKey,
    children,
    title,
    stickyHeader = true,
}: CampaignSettingsShellProps) {
    const { t } = useTranslation()
    const label = title ?? t('Campaign settings')
    const { token } = theme.useToken()

    const container = useRef<HTMLDivElement>(null)
    const [availableWidth, setAvailableWidth] = useState(0)
    const [open, setOpen] = useState(false)
    const size: NavigationSize = availableWidth >= 1000 ? 'wide' : 'small'

    useLayoutEffect(() => {
        const element = container.current
        if (!element) return

        const measure = () => {
            const width = element.getBoundingClientRect().width
            setAvailableWidth(width)
        }

        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        setOpen(false)
    }, [activeKey])

    useEffect(() => {
        if (size !== 'small') setOpen(false)
    }, [size])

    const links = items.map((item) => (
        <Link
            key={item.key}
            to={item.key}
            activeOptions={{ exact: true }}
            aria-current={item.key === activeKey ? 'page' : undefined}
            className="campaign-settings-link"
            onClick={() => {
                if (item.key === activeKey) setOpen(false)
            }}
        >
            {item.label}
        </Link>
    ))

    const styles = {
        '--settings-active-color': token.colorPrimary,
        '--settings-active-background': token.colorPrimaryBg,
        '--settings-hover-background': token.colorFillTertiary,
        ...(stickyHeader ? {} : { '--campaign-settings-header-height': '0px' }),
    } as CSSProperties

    return (
        <div
            ref={container}
            className="campaign-settings-shell"
            style={styles}
        >
            <div className="campaign-settings-grid">
                <div className="campaign-settings-navigation-host">
                    {size === 'small' ? (
                        <Button
                            block
                            icon={<MenuOutlined />}
                            onClick={() => setOpen(true)}
                            className="campaign-settings-drawer-trigger"
                            aria-expanded={open}
                            aria-haspopup="dialog"
                        >
                            <span className="campaign-settings-current-section">
                                <span>{label}</span>
                                <strong>{items.find((item) => item.key === activeKey)?.label}</strong>
                            </span>
                        </Button>
                    ) : (
                        <nav
                            aria-label={label}
                            className="campaign-settings-navigation campaign-settings-navigation-wide"
                        >
                            {links}
                        </nav>
                    )}
                </div>
                <div className="campaign-settings-content">{children}</div>
            </div>
            <Drawer
                title={label}
                placement="left"
                width="min(320px, 100vw)"
                open={open && size === 'small'}
                onClose={() => setOpen(false)}
                destroyOnHidden
            >
                <nav
                    aria-label={label}
                    className="campaign-settings-navigation campaign-settings-navigation-wide"
                    style={styles}
                >
                    {links}
                </nav>
            </Drawer>
        </div>
    )
}
